import type { PropsWithChildren } from 'react';
import { ChevronDownIcon } from '@heroicons/react/24/solid';
import { tv } from 'tailwind-variants';

import {
  reverseOrder,
  type Sort,
  SortOrder,
} from '../../../router/hooks/use-sort';

export type TableSortingProps<F extends string> = PropsWithChildren<{
  sort: Sort<F>;
  field: F;
  className?: string;
}>;

export function TableSorting<F extends string>({
  children,
  sort,
  field,
  className,
}: TableSortingProps<F>) {
  const isActive = field === sort.field;
  const currentOrder = isActive ? sort.order : SortOrder.ASC;
  const nextOrder = isActive ? reverseOrder[sort.order] : SortOrder.ASC;

  const style = tableSortingStyle({
    isActive,
    order: currentOrder,
  });

  return (
    <span
      className={style.container({ className })}
      onClick={function (e) {
        e.preventDefault();
        sort.update(field, nextOrder);
      }}
    >
      {children}
      <span className={style.iconContainer()}>
        <ChevronDownIcon aria-hidden="true" className={style.icon()} />
      </span>
    </span>
  );
}

const tableSortingStyle = tv({
  slots: {
    container: `
      group
      inline-flex
      items-center
      gap-2
    `,
    iconContainer: `
      flex-none
      rounded
      text-gray-400
    `,
    icon: `
      size-4
      cursor-pointer
    `,
  },
  variants: {
    order: {
      asc: {},
      desc: {
        icon: `rotate-180`,
      },
    },
    isActive: {
      false: {
        iconContainer: `
          invisible
          group-hover:visible
          group-focus:visible
        `,
      },
    },
  },
});
